/**
 * @author nick
 * @description 統一的 Storage 管理工具
 * - token 使用 cookie（用於 API 請求）
 * - 其他資料使用 localStorage
*/
import { toast } from 'react-toastify';

const STORAGE_KEYS = {
  // 使用者相關
  USER: 'user',
  ADMIN: 'admin',
  LANG: 'lang',
  JOB_SUBMIT_MODE: 'jobSubmitMode',
  GUIDE_OBJ: 'guideObj',
  ALERT_MESSAGE: 'alertMessage',
  SCHEDULE_RESOURCE_ITEM: 'scheduleResourceItem',
  API_TIME_STAMP: 'apiTimeStamp',
  USER_INVITE_CODE: 'userInviteCode',
  
  // Filter 相關
  FILTER_JOB: 'pai-job-filter',                    // JobDetail 的 Filter
  FILTER_FS_ITEM: 'pai-fs-item-filter',            // FsItemList 的 Filter
  FILTER_WITHDRAW_ORDER: 'cyi-withdraw-order-filter',  // WithdrawOrderManage 的 Filter
  FILTER_ORDER: 'cyi-order-filter',                // OrderManage 的 Filter
  FILTER_DEPOSIT: 'cyi-deposit-filter',            // DepositManage 的 Filter
  FILTER_WITHDRAW: 'cyi-withdraw-filter'           // WithdrawManage 的 Filter

};

class StorageManager {
  /**
   * @description 取得資料（優先從 localStorage，找不到再從 cookie）
   * @param {string} key - 儲存的 key
   * @param {any} defaultValue - 預設值
   * @returns {any}
   */
  get(key, defaultValue = null) {
    try {
      // 1. 優先從 localStorage 取得
      const localValue = localStorage.getItem(key);
      if (localValue !== null) {
        try {
          return JSON.parse(localValue);
        } catch {
          return localValue;
        }
      }

      // 2. 如果 localStorage 沒有，嘗試從 cookie 遷移
      const cookieValue = this._getCookie(key);
      if (cookieValue) {
        // 遷移到 localStorage
        this.set(key, cookieValue);
        // 刪除舊的 cookie
        this._removeCookie(key);
        return cookieValue;
      }

      return defaultValue;
    } catch (error) {
      toast.error(`Error getting ${key} from storage:`, error);
      return defaultValue;
    }
  }

  /**
   * @description 儲存資料到 localStorage
   * @param {string} key - 儲存的 key
   * @param {any} value - 要儲存的值
   */
  set(key, value) {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, stringValue);
    } catch (error) {
      toast.error(`Error setting ${key} to storage:`, error);
    }
  }

  /**
   * @description 移除資料
   * @param {string} key - 要移除的 key
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
      // 同時清除可能存在的 cookie
      this._removeCookie(key);
    } catch (error) {
      toast.error(`Error removing ${key} from storage:`, error);
    }
  }

  /**
   * @description 清空所有資料（除了 token）
   */
  clear() {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        this.remove(key);
      });
    } catch (error) {
      toast.error('Error clearing storage:', error);
    }
  }

  /**
   * @description 從 cookie 取得值（私有方法）
   * @private
   */
  _getCookie(key) {
    const name = key + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    
    for (let i = 0; i < cookieArray.length; i++) {
      const cookie = cookieArray[i].trim();
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length, cookie.length);
      }
    }
    return null;
  }

  /**
   * @description 刪除 cookie（私有方法）
   * @private
   */
  _removeCookie(key) {
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
}

export const storage = new StorageManager();
export { STORAGE_KEYS };
